import React, { createContext, useContext, useEffect, useState } from "react";

import api from "../../services/api";
import { getPublicFileUrl } from "../../config";

const WhiteLabelContext = createContext({
  appName: "WhaTicket",
  logoUrl: "",
  backgroundUrl: "",
  primaryColor: "",
  recarregar: () => {},
});

// Converte "#38bdf8" no formato que o tema usa nas variáveis CSS: "H S% L%".
// O Tailwind aqui compõe as cores com hsl(var(--primary)), então gravar o hex
// direto não pinta nada.
const hexParaHsl = (hex) => {
  const limpo = String(hex || "").replace("#", "").trim();

  if (!/^[0-9a-fA-F]{6}$/.test(limpo)) return null;

  const r = parseInt(limpo.slice(0, 2), 16) / 255;
  const g = parseInt(limpo.slice(2, 4), 16) / 255;
  const b = parseInt(limpo.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) return `0 0% ${Math.round(l * 100)}%`;

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Texto legível sobre a cor escolhida. Uma marca escura precisa de letra
// clara e vice-versa — fixar branco deixaria o botão ilegível em amarelo.
const contrasteDe = (hsl) => {
  const luminancia = Number(String(hsl).split(" ")[2]?.replace("%", "") || 50);
  return luminancia > 60 ? "222 47% 11%" : "0 0% 100%";
};

export const WhiteLabelProvider = ({ children }) => {
  const [marca, setMarca] = useState({
    appName: "WhaTicket",
    logoUrl: "",
    backgroundUrl: "",
    primaryColor: "",
  });

  const carregar = async () => {
    try {
      const { data } = await api.get("/settings/white-label");
      setMarca({
        appName: data?.appName || "WhaTicket",
        logoUrl: data?.logoUrl || "",
        backgroundUrl: data?.backgroundUrl || "",
        primaryColor: data?.primaryColor || "",
      });
    } catch (err) {
      // Sem whitelabel o sistema usa o visual padrão — não é motivo para
      // travar a tela.
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  // Aplica no documento: título da aba e a cor primária do tema.
  useEffect(() => {
    document.title = marca.appName;

    const hsl = hexParaHsl(marca.primaryColor);

    if (!hsl) return;

    const raiz = document.documentElement;
    raiz.style.setProperty("--primary", hsl);
    raiz.style.setProperty("--primary-foreground", contrasteDe(hsl));
    // O anel de foco acompanha a marca, senão fica um azul solto na tela.
    raiz.style.setProperty("--ring", hsl);
  }, [marca.appName, marca.primaryColor]);

  const logoCompleta = getPublicFileUrl(marca.logoUrl);

  const fundoCompleto = getPublicFileUrl(marca.backgroundUrl);

  return (
    <WhiteLabelContext.Provider
      value={{
        ...marca,
        logoUrl: logoCompleta,
        backgroundUrl: fundoCompleto,
        recarregar: carregar,
      }}
    >
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = () => useContext(WhiteLabelContext);

export default WhiteLabelContext;
